import { Controller } from "@hotwired/stimulus";
import { MARK_CLEAN_EVENT, MARK_DIRTY_EVENT } from "../settings_shell.js";

// Settings shell (partials/components/_settings_shell.html): one section at a time, section key <-> URL hash,
// dirty tracking + per-section save bar. NO saving logic here: the shell dispatches `settings-shell:save` and
// `settings-shell:cancel` (detail {key}); the page controller does the work and then calls markClean(key)
// (via ../settings_shell.js). A section opts into dirty tracking with `data-settings-shell-dirty-watch`.
export default class extends Controller {
  static targets = [
    "tabBtn", // nav buttons (data-tab = section key)
    "panel",  // section panels (data-tab = section key)
    "bar",    // save bars (data-tab = section key), hidden until the section is dirty
  ];

  connect() {
    this._dirty = new Set();
    this._onInput = (event) => this.#touched(event);
    this._onClean = (event) => this.markClean(event.detail?.key);
    this.element.addEventListener("input", this._onInput);
    this.element.addEventListener("change", this._onInput);
    this._onDirty = (event) => this.#setDirty(event.detail?.key, true);
    this.element.addEventListener(MARK_CLEAN_EVENT, this._onClean);
    this.element.addEventListener(MARK_DIRTY_EVENT, this._onDirty);
    this.#showTab(this.#hashTab(), { writeHash: false });
  }

  disconnect() {
    this.element.removeEventListener("input", this._onInput);
    this.element.removeEventListener("change", this._onInput);
    this.element.removeEventListener(MARK_CLEAN_EVENT, this._onClean);
    this.element.removeEventListener(MARK_DIRTY_EVENT, this._onDirty);
  }

  // ─── Sections ────────────────────────────────────────────────────────────

  selectTab(event) {
    const tab = event.currentTarget.dataset.tab;
    if (tab) this.#showTab(tab, { writeHash: true });
  }

  // Section key from location.hash; unknown or empty → the first section.
  #hashTab() {
    const keys = this.panelTargets.map((panel) => panel.dataset.tab);
    let hash = "";
    try {
      hash = decodeURIComponent((window.location.hash || "").replace(/^#/, ""));
    } catch {
      hash = "";
    }
    return keys.includes(hash) ? hash : keys[0];
  }

  #showTab(tab, { writeHash }) {
    if (!tab || this.panelTargets.length === 0) return;
    for (const btn of this.tabBtnTargets) {
      const active = btn.dataset.tab === tab;
      btn.classList.toggle("is-active", active);
      btn.setAttribute("aria-selected", active ? "true" : "false");
    }
    for (const panel of this.panelTargets) panel.hidden = panel.dataset.tab !== tab;
    if (writeHash) {
      try {
        window.history.replaceState(null, "", `#${tab}`);
      } catch {
        // history unavailable — the section is switched anyway
      }
    }
  }

  // ─── Dirty state ─────────────────────────────────────────────────────────

  #touched(event) {
    const panel = event.target.closest?.("[data-settings-shell-dirty-watch]");
    if (panel && this.element.contains(panel)) this.#setDirty(panel.dataset.tab, true);
  }

  #setDirty(key, dirty) {
    if (!key) return;
    if (dirty) this._dirty.add(key);
    else this._dirty.delete(key);
    for (const bar of this.barTargets) if (bar.dataset.tab === key) bar.hidden = !dirty;
    for (const btn of this.tabBtnTargets) if (btn.dataset.tab === key) btn.classList.toggle("is-dirty", dirty);
  }

  // Public: the page controller calls it after it saved or reset the section.
  markClean(key) {
    this.#setDirty(key, false);
  }

  isDirty(key) {
    return this._dirty.has(key);
  }

  // ─── Save bar → events for the page controllers ──────────────────────────

  save(event) {
    this.dispatch("save", { detail: { key: event.currentTarget.dataset.tab } });
  }

  cancel(event) {
    this.dispatch("cancel", { detail: { key: event.currentTarget.dataset.tab } });
  }
}
